import { useState, useRef, useEffect } from "react";
import { Send, Bot, User, AlertCircle } from "lucide-react";

const API = "https://crumpled-modify-blog.ngrok-free.dev";

function parseNota(texto) {
  const m = texto.match(/NOTA\s*FINAL[:\s]*([0-9]+[.,][0-9]+|[0-9]+)/i);
  return m ? parseFloat(m[1].replace(",", ".")).toFixed(1) : null;
}

function parseTitulo(texto) {
  const m = texto.match(/TÍ?TULO[:\s]*(.+)/i);
  return m ? m[1].trim().slice(0, 60) : null;
}

function NotaCard({ nota }) {
  const n = parseFloat(nota);
  const [cor, bg, label] =
    n >= 8 ? ["#10b981", "#d1fae5", "Aprovado com destaque"] :
    n >= 6 ? ["#f59e0b", "#fef3c7", "Aprovado"] :
    ["#ef4444", "#fee2e2", "Necessita revisão"];

  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 16,
      background: bg, border: `1.5px solid ${cor}30`,
      borderRadius: 12, padding: "12px 20px", marginBottom: 14
    }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 32, fontWeight: 800, color: cor, lineHeight: 1 }}>{nota}</div>
        <div style={{ fontSize: 11, color: cor, fontWeight: 500 }}>/10</div>
      </div>
      <div style={{ width: 1, height: 36, background: cor + "40" }} />
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: cor }}>{label}</div>
        <div style={{ fontSize: 11, color: cor, opacity: 0.8 }}>Nota final PROBEX</div>
      </div>
    </div>
  );
}

function Bolha({ msg }) {
  const isUser = msg.role === "user";
  const nota = !isUser ? parseNota(msg.content) : null;
  const texto = !isUser && nota
    ? msg.content.replace(/NOTA\s*FINAL[:\s]*[0-9]+[.,]?[0-9]*/i, "").trim()
    : msg.content;

  return (
    <div style={{
      display: "flex", gap: 10, marginBottom: 20,
      flexDirection: isUser ? "row-reverse" : "row", alignItems: "flex-start"
    }}>
      <div style={{
        width: 34, height: 34, borderRadius: 10, flexShrink: 0,
        background: isUser ? "#e0e7ff" : "var(--primary)",
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "var(--shadow)"
      }}>
        {isUser ? <User size={16} color="var(--primary)" /> : <Bot size={16} color="#fff" />}
      </div>
      <div style={{
        maxWidth: "78%",
        background: isUser ? "var(--primary)" : "var(--bg2)",
        color: isUser ? "#fff" : "var(--text)",
        borderRadius: isUser ? "16px 4px 16px 16px" : "4px 16px 16px 16px",
        padding: "14px 18px",
        boxShadow: "var(--shadow)",
        border: isUser ? "none" : "1px solid var(--border)",
        fontSize: 14, lineHeight: 1.7
      }}>
        {nota && <NotaCard nota={nota} />}
        <div style={{ whiteSpace: "pre-wrap" }}>{texto}</div>
        <div style={{ fontSize: 11, marginTop: 6, opacity: 0.5, textAlign: isUser ? "left" : "right" }}>
          {new Date(msg.ts).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
        </div>
      </div>
    </div>
  );
}

function Digitando() {
  return (
    <div style={{ display: "flex", gap: 10, marginBottom: 20, alignItems: "flex-start" }}>
      <div style={{
        width: 34, height: 34, borderRadius: 10, background: "var(--primary)",
        display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "var(--shadow)"
      }}>
        <Bot size={16} color="#fff" />
      </div>
      <div style={{
        background: "var(--bg2)", border: "1px solid var(--border)",
        borderRadius: "4px 16px 16px 16px", padding: "16px 20px",
        boxShadow: "var(--shadow)", display: "flex", gap: 5, alignItems: "center"
      }}>
        {[0,1,2].map(i => (
          <div key={i} style={{
            width: 8, height: 8, borderRadius: "50%", background: "var(--primary)",
            animation: `bounce 1.2s ease-in-out ${i*0.2}s infinite`
          }} />
        ))}
      </div>
    </div>
  );
}

export default function ChatAvaliador({ onSalvar, edital, setEdital }) {
  const [msgs, setMsgs] = useState([{
    role: "assistant", ts: Date.now(),
    content: "Olá! Sou o avaliador de projetos de extensão PROBEX/UFPB.\n\nCole os dados completos do projeto — título, resumo, justificativa, objetivos, metodologia e resultados esperados — e vou retornar a nota final com parecer detalhado por critério."
  }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const bottomRef = useRef();
  const textareaRef = useRef();

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs, loading]);

  async function enviar() {
    if (!input.trim() || loading) return;
    const texto = input.trim();
    setInput("");
    setErro(null);
    const userMsg = { role: "user", ts: Date.now(), content: texto };
    setMsgs(m => [...m, userMsg]);
    setLoading(true);
    try {
      const res = await fetch(`${API}/avaliar-texto`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ texto, edital })
      });
      if (!res.ok) throw new Error("Erro na API");
      const data = await res.json();
      const asstMsg = { role: "assistant", ts: Date.now(), content: data.avaliacao };
      setMsgs(m => [...m, asstMsg]);
      const nota = parseNota(data.avaliacao);
      const titulo = parseTitulo(texto);
      if (nota) onSalvar({ titulo, nota, edital, projeto: texto, avaliacao: data.avaliacao });
    } catch {
      setErro("Não foi possível conectar à API. Verifique se o servidor está rodando na porta 8000.");
    }
    setLoading(false);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", background: "var(--bg)" }}>
      {/* Topbar */}
      <div style={{
        background: "var(--bg2)", borderBottom: "1px solid var(--border)",
        padding: "14px 28px", display: "flex", alignItems: "center", gap: 16,
        boxShadow: "var(--shadow)"
      }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 15 }}>Avaliação de Projeto</div>
          <div style={{ fontSize: 12, color: "var(--text3)" }}>Cole os dados do projeto e receba nota + parecer</div>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
          <label style={{ fontSize: 12, color: "var(--text2)", fontWeight: 500 }}>Edital:</label>
          <select value={edital} onChange={e => setEdital(e.target.value)} style={{
            fontSize: 13, padding: "6px 12px", borderRadius: 8,
            border: "1px solid var(--border)", background: "var(--bg3)",
            color: "var(--text)", fontFamily: "inherit"
          }}>
            {["PROBEX 2017","PROBEX 2018","PROBEX 2019","PROBEX 2024","PROBEX 2025"].map(e => (
              <option key={e}>{e}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Msgs */}
      <div style={{ flex: 1, overflowY: "auto", padding: "28px 32px" }}>
        <div style={{ maxWidth: 820, margin: "0 auto" }}>
          {msgs.map((m, i) => <Bolha key={i} msg={m} />)}
          {loading && <Digitando />}
          {erro && (
            <div style={{
              display: "flex", gap: 10, alignItems: "flex-start",
              background: "#fee2e2", border: "1px solid #fca5a5",
              borderRadius: 12, padding: "14px 18px", marginBottom: 16
            }}>
              <AlertCircle size={16} color="#ef4444" style={{ flexShrink: 0, marginTop: 2 }} />
              <span style={{ fontSize: 13, color: "#b91c1c" }}>{erro}</span>
            </div>
          )}
          <div ref={bottomRef} />
        </div>
      </div>

      {/* Input */}
      <div style={{
        background: "var(--bg2)", borderTop: "1px solid var(--border)", padding: "16px 32px"
      }}>
        <div style={{ maxWidth: 820, margin: "0 auto", display: "flex", gap: 12, alignItems: "flex-end" }}>
          <div style={{ flex: 1, position: "relative" }}>
            <textarea ref={textareaRef} value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); enviar(); }}}
              placeholder="Cole os dados do projeto aqui... (Enter para enviar · Shift+Enter para nova linha)"
              rows={3}
              style={{
                width: "100%", resize: "none", borderRadius: 12, padding: "14px 18px",
                fontSize: 14, lineHeight: 1.6, border: "1.5px solid var(--border)",
                background: "var(--bg3)", color: "var(--text)", fontFamily: "inherit",
                transition: "border-color .15s", boxShadow: "var(--shadow)"
              }}
              onFocus={e => e.target.style.borderColor = "var(--primary)"}
              onBlur={e => e.target.style.borderColor = "var(--border)"}
            />
          </div>
          <button onClick={enviar} disabled={loading || !input.trim()} style={{
            width: 48, height: 48, borderRadius: 12, border: "none",
            background: loading || !input.trim() ? "#c7d2fe" : "var(--primary)",
            color: "#fff", display: "flex", alignItems: "center",
            justifyContent: "center", transition: "all .15s",
            boxShadow: loading || !input.trim() ? "none" : "0 4px 12px rgba(99,102,241,0.4)"
          }}>
            <Send size={18} />
          </button>
        </div>
        <div style={{ maxWidth: 820, margin: "6px auto 0", fontSize: 11, color: "var(--text3)", paddingLeft: 2 }}>
          IA rodando localmente · seus dados nunca saem da sua máquina
        </div>
      </div>

      <style>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); opacity: 0.4; }
          50% { transform: translateY(-5px); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
